import { compact, orderBy } from "lodash";
import React, { FunctionComponent } from "react";
import { useIntl } from "react-intl";
import { ReactComponent as IconColor } from "../../../../icons/16x16/color-picker.svg";
import { TemplateLocale } from "../../../../types/customization/common";
import { Configuration } from "../../../../types/customization/configuration";
import { Template } from "../../../../types/customization/template";
import { imageUrlFromAssetDefinition } from "../../../../utils/customization/asset";
import { hasSelectableOption } from "../../../../utils/customization/configuration";
import { isMobileSize } from "../../../../utils/customization/device";
import { findAssetDefinition } from "../../../../utils/customization/finders";
import { findLocalizedContent } from "../../../../utils/customization/localized";
import OptionList from "../panels/OptionList";
import "./style.scss";

interface Props {
  configuration: Configuration | undefined;
  locale: TemplateLocale;
  template: Template;
}

const OPTION_ASSET_WIDTH = 300;
const ICON_ASSET_WIDTH = 16;

const ProductOptions: FunctionComponent<Props> = ({ configuration, locale, template }) => {
  const intl = useIntl();

  const assetUrlTemplate = configuration?.assetUrlTemplate;
  const hasOptions = hasSelectableOption(configuration);

  const productOptions = template.optionDefinitions.map((optionDefinition) => {
    if (optionDefinition.optionTag === "product_tag" && configuration?.options && hasOptions) {
      const assetDefinition = optionDefinition.iconAssetKey
        ? findAssetDefinition(optionDefinition.iconAssetKey, template)
        : null;
      const iconUrl = assetDefinition
        ? imageUrlFromAssetDefinition(assetDefinition, ICON_ASSET_WIDTH, assetUrlTemplate)
        : null;
      const selectedOption = configuration.options.find(
        (option) => option.tag === optionDefinition.optionTag
      );
      if (!selectedOption) {
        return null;
      }

      const optionTitle =
        !!optionDefinition.optionTitle &&
        optionDefinition.optionTitle.length > 0 &&
        findLocalizedContent(optionDefinition.optionTitle, locale);
      const title =
        !!optionTitle && optionTitle.content
          ? optionTitle.content
          : intl.formatMessage({ id: "kustom_options" });

      const orderedOptionValues = orderBy(selectedOption.values, ["label", "value"], "asc");

      const optionValuesFormatted = orderedOptionValues.map((option) => {
        const optionValueDefinition = optionDefinition.optionChoices.find(
          (valueDefinition) => valueDefinition.value === option.value
        );
        if (!optionValueDefinition) {
          return null;
        }
        const optionValueTitle =
          !!optionValueDefinition.label &&
          optionValueDefinition.label.length > 0 &&
          findLocalizedContent(optionValueDefinition.label, locale);
        const optionValueTitleContent =
          option.label ||
          (optionValueTitle && optionValueTitle.content ? optionValueTitle.content : "");

        const assetValueDefinition = optionValueDefinition.assetKey
          ? findAssetDefinition(optionValueDefinition.assetKey, template)
          : null;
        const imageValueUrl = assetValueDefinition
          ? imageUrlFromAssetDefinition(assetValueDefinition, OPTION_ASSET_WIDTH, assetUrlTemplate)
          : undefined;

        const onSelectHandler = () => {
          selectedOption.onChange(option.value);
        };

        return {
          assetUrl: imageValueUrl,
          label: optionValueTitleContent,
          onSelect: onSelectHandler,
          price: option.price,
          value: option.value,
        };
      });

      const compactedOptionValuesFormatted = compact(optionValuesFormatted);

      if (compactedOptionValuesFormatted.length === 0) {
        return null;
      }

      return (
        <OptionList
          closable={isMobileSize()}
          key={`${selectedOption.tag}${selectedOption.value}`}
          icon={iconUrl !== null ? <img src={iconUrl} alt={title} /> : <IconColor />}
          optionItems={compactedOptionValuesFormatted}
          selectedValue={selectedOption.value}
          title={title}
        />
      );
    } else {
      // todo: manage other options ?
      return null;
    }
  });

  return <div className="ProductOptions">{productOptions}</div>;
};

export default ProductOptions;
